/**
 * When infinite-scroll reveals the next batch while the user is parked at the
 * footer / loader, the scroll root often stays pinned to the bottom and the
 * sentinel keeps firing. Scroll the first tile of the new batch into view so
 * the viewport lands on fresh content instead of the footer.
 */

const SCROLL_ROOT_ID = "master-template-scroll-root";

let editorPreviewScrollLocked = false;

export function setEditorPreviewScrollLocked(locked: boolean) {
  editorPreviewScrollLocked = locked;
}

export function isEditorPreviewScrollLocked() {
  return editorPreviewScrollLocked;
}

export function getTemplateScrollRoot(): HTMLElement | null {
  return document.getElementById(SCROLL_ROOT_ID);
}

/** Preview scrollport in the website editor (dashboard mobile may omit the id). */
export function getEditorPreviewScrollRoot(): HTMLElement | null {
  const mobileFrame = document.querySelector(
    ".mte-root--we .mte-wem-canvas .mte-bc-frame--mobile",
  ) as HTMLElement | null;
  if (mobileFrame) return mobileFrame;
  return (
    getTemplateScrollRoot() ||
    (document.querySelector(
      ".mte-root--we .mte-bc-frame--mobile",
    ) as HTMLElement | null) ||
    (document.querySelector(
      ".mte-root--we .mte-root-scroll--mobile",
    ) as HTMLElement | null) ||
    (document.querySelector(
      ".mte-root--we .mte-root-scroll",
    ) as HTMLElement | null)
  );
}

function previewScrollerFor(el: HTMLElement): HTMLElement | null {
  return (
    (el.closest(".mte-bc-frame") as HTMLElement | null) ||
    getEditorPreviewScrollRoot()
  );
}

/**
 * Scroll a section/ghost into the free band above the mobile edit sheet so the
 * preview stays visible while configuring. Falls back to center on desktop.
 */
/**
 * Retry until a newly added section mounts (Suspense / refreshLiveData lag),
 * then scroll it into the preview. Used after Add component.
 */
export function scrollEditorPreviewSectionWhenReady(
  sectionId: string,
  opts?: { attempts?: number; intervalMs?: number },
): void {
  const id = String(sectionId || "").trim();
  if (!id || editorPreviewScrollLocked) return;
  const attempts = Math.max(1, opts?.attempts ?? 12);
  const intervalMs = Math.max(40, opts?.intervalMs ?? 120);
  const selector = `[data-template-section-id="${id}"], [data-template-section="${id}"], [data-mte-reorder-id="${id}"]`;

  const tryOnce = (left: number) => {
    if (editorPreviewScrollLocked) return;
    const el = document.querySelector(selector);
    if (el instanceof HTMLElement) {
      scrollEditorPreviewTargetIntoView(el);
      // Second pass after layout settles (images / marquees).
      window.setTimeout(() => scrollEditorPreviewTargetIntoView(el), 180);
      return;
    }
    if (left <= 1) return;
    window.setTimeout(() => tryOnce(left - 1), intervalMs);
  };

  window.setTimeout(() => tryOnce(attempts), 40);
}

export function scrollEditorPreviewTargetIntoView(
  el: Element | null | undefined,
): void {
  if (!el || !(el instanceof HTMLElement) || editorPreviewScrollLocked) return;
  const sheet = document.querySelector(".mte-wem-sheet") as HTMLElement | null;
  const scroller = previewScrollerFor(el);
  if (sheet && scroller) {
    const visibleBottom = sheet.getBoundingClientRect().top - 12;
    const rect = el.getBoundingClientRect();
    let delta: number;
    if (el.classList.contains("mte-we-insert")) {
      delta = rect.bottom - visibleBottom;
    } else if (
      el.classList.contains("mte-we-ghost") ||
      el.classList.contains("mte-lib-draft-ghost")
    ) {
      const scRect = scroller.getBoundingClientRect();
      delta = rect.top - (scRect.top + 10);
    } else {
      const targetY = Math.max(24, visibleBottom * 0.42);
      delta = rect.top - targetY;
    }
    if (Math.abs(delta) < 10) return;
    scroller.scrollTo({
      top: Math.max(0, scroller.scrollTop + delta),
      behavior: "smooth",
    });
    return;
  }
  if (scroller) {
    const elRect = el.getBoundingClientRect();
    const scRect = scroller.getBoundingClientRect();
    const visible = Math.min(elRect.height, scRect.height);
    const delta =
      elRect.top - scRect.top - Math.max(16, (scRect.height - visible) / 2);
    if (Math.abs(delta) < 8) return;
    scroller.scrollTo({
      top: Math.max(0, scroller.scrollTop + delta),
      behavior: "smooth",
    });
    return;
  }
  el.scrollIntoView({ behavior: "smooth", block: "center" });
}

export function scrollActiveEditorPreviewIntoView(
  opts?: PreviewTargetOpts,
): void {
  const el = findEditorInspectorPreviewTarget(opts);
  if (!el || editorPreviewScrollLocked) return;
  scrollEditorPreviewTargetIntoView(el);
}

type PreviewTargetOpts = {
  insertAt?: number;
  sectionId?: string;
  idx?: number;
};

function activePreviewRoot(): ParentNode {
  return (
    (document.querySelector(
      ".mte-tpl-page-body-layer--active, [data-template-page-active='true']",
    ) as HTMLElement | null) || document
  );
}

function visiblePreviewEl(
  root: ParentNode,
  selector: string,
): HTMLElement | null {
  const nodes = root.querySelectorAll(selector);
  for (let i = 0; i < nodes.length; i += 1) {
    const node = nodes[i];
    if (!(node instanceof HTMLElement)) continue;
    if (node.getBoundingClientRect().height >= 1) return node;
  }
  return null;
}

/** Resolve the active Add ghost or Edit selection for preview scroll. */
export function findEditorInspectorPreviewTarget(
  opts?: PreviewTargetOpts,
): HTMLElement | null {
  const root = activePreviewRoot();
  const insertAt = opts?.insertAt;
  const sectionId = String(opts?.sectionId || "").trim();
  const idx = opts?.idx;
  const hasIndexTarget =
    typeof insertAt === "number" ||
    Boolean(sectionId) ||
    typeof idx === "number";

  if (sectionId) {
    const esc =
      typeof CSS !== "undefined" && typeof CSS.escape === "function"
        ? CSS.escape(sectionId)
        : sectionId.replace(/"/g, '\\"');
    const byId =
      visiblePreviewEl(
        root,
        `[data-mte-reorder-id="${esc}"], [data-template-section-id="${esc}"]`,
      ) ||
      visiblePreviewEl(
        document,
        `[data-mte-reorder-id="${esc}"], [data-template-section-id="${esc}"]`,
      );
    if (byId) return byId;
  }

  if (typeof idx === "number" && idx >= 0) {
    const sections = root.querySelectorAll(".mte-we-sec[data-mte-reorder-id]");
    const byIdx = sections[idx];
    if (
      byIdx instanceof HTMLElement &&
      byIdx.getBoundingClientRect().height >= 1
    ) {
      return byIdx;
    }
  }

  const ghost =
    visiblePreviewEl(root, ".mte-we-ghost, .mte-lib-draft-ghost") ||
    visiblePreviewEl(document, ".mte-we-ghost, .mte-lib-draft-ghost");
  if (ghost) return ghost;

  if (typeof insertAt === "number") {
    if (insertAt < 0) {
      const end =
        visiblePreviewEl(
          root,
          ".mte-we-insert--standalone.is-hot, .mte-we-insert--standalone.is-touch",
        ) ||
        visiblePreviewEl(
          document,
          ".mte-we-insert--standalone.is-hot, .mte-we-insert--standalone.is-touch",
        );
      if (end) return end;
    } else {
      const atSlot =
        visiblePreviewEl(
          root,
          `[data-mte-insert-at="${insertAt}"].mte-we-insert`,
        ) ||
        visiblePreviewEl(
          document,
          `[data-mte-insert-at="${insertAt}"].mte-we-insert`,
        );
      if (atSlot) return atSlot;
    }
  }

  const selected =
    visiblePreviewEl(root, ".mte-we-sec.is-selected") ||
    visiblePreviewEl(
      root,
      ".mte-tpl-link-in-bio-stack__block.is-selected, .mte-lib-item.is-selected",
    ) ||
    visiblePreviewEl(document, ".mte-we-sec.is-selected") ||
    visiblePreviewEl(
      document,
      ".mte-tpl-link-in-bio-stack__block.is-selected, .mte-lib-item.is-selected",
    );
  if (hasIndexTarget) return selected;

  const insert = visiblePreviewEl(
    root,
    ".mte-we-insert.is-hot:not(.is-dormant), .mte-we-insert.is-touch:not(.is-dormant)",
  );
  if (insert) return insert;

  return selected;
}


export function isNearTemplateScrollBottom(
  root: HTMLElement,
  thresholdPx = 160,
): boolean {
  return root.scrollHeight - root.scrollTop - root.clientHeight <= thresholdPx;
}

/**
 * Find the DOM node for the first item of a newly revealed batch.
 * Supports shop cards, content feed tiles, and recent-timeline fallbacks.
 */
export function findTemplateBatchAnchor(
  container: ParentNode | null | undefined,
  startIndex: number,
): Element | null {
  if (!container || startIndex < 0) return null;
  // Masonry packs into columns — prefer stable original index marker.
  const byMasonryIndex = (container as Element).querySelector?.(
    `[data-tpl-masonry-index="${startIndex}"]`,
  );
  if (byMasonryIndex) {
    return (
      byMasonryIndex.querySelector(
        ".mte-tpl-shop-card-link, .mte-tpl-shop-card",
      ) ?? byMasonryIndex
    );
  }
  const tiles = container.querySelectorAll(
    [
      ":scope > .mte-tpl-shop-card-link",
      ":scope > .mte-tpl-shop-card",
      ":scope > .mte-tpl-rc-feed-card",
      ":scope > .mte-tpl-rc-tl-row",
      ".mte-tpl-grid-tile",
      ".mte-tpl-shop-card-link",
      ".mte-tpl-rc-feed-card",
      ".mte-tpl-rc-tl-row",
    ].join(", "),
  );
  return tiles[startIndex] ?? null;
}

export function scrollTemplateNewBatchIntoView(
  anchor: Element | null | undefined,
  options?: {
    behavior?: ScrollBehavior;
    offsetPx?: number;
    /** Only scroll when the user is near the bottom (default true). */
    requireNearBottom?: boolean;
    nearBottomPx?: number;
  },
): void {
  const root = getTemplateScrollRoot();
  if (!root || !anchor) return;

  const requireNearBottom = options?.requireNearBottom !== false;
  if (
    requireNearBottom &&
    !isNearTemplateScrollBottom(root, options?.nearBottomPx ?? 160)
  ) {
    return;
  }

  const prefersReduced =
    typeof window !== "undefined" &&
    window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;
  const behavior: ScrollBehavior =
    options?.behavior ?? (prefersReduced ? "auto" : "smooth");
  const offsetPx = options?.offsetPx ?? 16;

  const rootRect = root.getBoundingClientRect();
  const anchorRect = anchor.getBoundingClientRect();
  const delta = anchorRect.top - rootRect.top - offsetPx;
  if (Math.abs(delta) < 8) return;

  root.scrollTo({
    top: Math.max(0, root.scrollTop + delta),
    behavior,
  });
}

/** After React paints the new batch, scroll its first tile into view. */
export function scheduleScrollTemplateNewBatchIntoView(
  getAnchor: () => Element | null | undefined,
  options?: Parameters<typeof scrollTemplateNewBatchIntoView>[1],
): void {
  const run = () => {
    scrollTemplateNewBatchIntoView(getAnchor(), options);
  };
  // Double rAF: wait for layout after the batch commits.
  requestAnimationFrame(() => {
    requestAnimationFrame(run);
  });
}
